import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { CheckCircle2, Package, CreditCard, Download } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { getOrderConfirmationSummary } from "@/lib/order-confirmation.functions";

export const Route = createFileRoute("/bedankt")({
  validateSearch: (s: Record<string, unknown>) => ({
    order: typeof s.order === "string" ? s.order : "",
  }),
  head: () => ({
    meta: [
      { title: "Bedankt voor je bestelling — Elvion Group Nederland" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ThanksPage,
});

const METHOD_LABEL: Record<string, string> = {
  ideal: "iDEAL",
  creditcard: "Creditcard",
  bancontact: "Bancontact",
  applepay: "Apple Pay",
  banktransfer: "Bankoverschrijving",
  sofort: "SOFORT Banking",
  przelewy24: "Przelewy24",
  belfius: "Belfius",
  kbc: "KBC/CBC",
  eps: "EPS",
  giropay: "Giropay",
};

function ThanksPage() {
  const { order } = Route.useSearch();
  const lookup = useServerFn(getOrderConfirmationSummary);
  const { data: summary } = useQuery({
    queryKey: ["order-confirmation-summary", order],
    enabled: !!order,
    queryFn: () => lookup({ data: { orderNumber: order } }),
    refetchInterval: (q) => {
      const d = q.state.data;
      // Blijf pollen tot Mollie de status naar 'paid' zet (webhook kan even duren)
      return d && d.payment_status === "paid" ? false : 5000;
    },
    staleTime: 0,
  });

  const method =
    summary?.method != null ? (METHOD_LABEL[summary.method] ?? summary.method) : null;

  return (
    <>
      <Header />
      <main className="bg-background">
        <section className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-6">
          <CheckCircle2 className="mx-auto size-14 text-green-600" />
          <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
            Bedankt voor je bestelling!
          </h1>
          {order && (
            <p className="mt-3 text-text-secondary">
              Je ordernummer is <span className="font-semibold text-foreground">{order}</span>.
            </p>
          )}
          <p className="mt-4 text-sm text-text-secondary">
            Na een succesvolle betaling ontvang je een bevestiging per e-mail. Bij afhalen in
            Wageningen voldoe je het restbedrag bij ophalen. Je kunt de status van je bestelling
            altijd hieronder volgen.
          </p>

          {summary && (summary.mollie_payment_id || method) && (
            <div className="mx-auto mt-8 max-w-md rounded-2xl border border-border bg-card p-5 text-left shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <CreditCard className="size-4 text-orange" />
                Betaalgegevens
              </div>
              <p className="mt-1 text-xs text-text-secondary">
                Bewaar deze gegevens; je kunt hiermee je betaling direct herkennen op je bankafschrift.
              </p>
              <dl className="mt-3 grid gap-2 text-sm">
                {summary.mollie_payment_id && (
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-secondary">Transactie-ID</dt>
                    <dd className="font-mono text-xs font-semibold break-all text-right">
                      {summary.mollie_payment_id}
                    </dd>
                  </div>
                )}
                {method && (
                  <div className="flex items-baseline justify-between gap-3">
                    <dt className="text-text-secondary">Betaalmethode</dt>
                    <dd className="font-semibold">{method}</dd>
                  </div>
                )}
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-text-secondary">Betaalstatus</dt>
                  <dd className="font-semibold capitalize">
                    {summary.payment_status === "paid" ? "Voldaan" : summary.payment_status}
                  </dd>
                </div>
              </dl>
              <div className="mt-4">
                <Button asChild size="sm" variant="outline" className="w-full">
                  <a
                    href={`/api/public/orders/betaalspecificatie?order=${encodeURIComponent(order)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <Download className="mr-2 size-4" /> Download betaalspecificatie (PDF)
                  </a>
                </Button>
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild>
              <Link to="/order-status">
                <Package className="mr-2 size-4" /> Bestelstatus bekijken
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/c">Verder winkelen</Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
